"use client";

import type { RefObject } from "react";
import { useEffect } from "react";
import { CWITNESS_SCROLL_BOUNDARY_PX } from "./constants";

interface UseMobileScrollChainInput {
  innerElement: HTMLDivElement | null;
  isMobile: boolean;
  outerScrollRef: RefObject<HTMLElement | null> | null;
  shouldRenderContent: boolean;
}

interface ScrollChainElements {
  inner: HTMLDivElement;
  outer: HTMLElement;
}

type ResolveScrollChainElementsInput = UseMobileScrollChainInput;

interface ScrollBoundaryState {
  atBottom: boolean;
  atTop: boolean;
}

interface ResolvePresentScrollElementsInput {
  innerElement: HTMLDivElement | null;
  outerElement: HTMLElement | null;
}

interface ResolveOuterScrollElementInput {
  outerScrollRef: RefObject<HTMLElement | null> | null;
}

interface ShouldAttachMobileScrollChainInput {
  isMobile: boolean;
  shouldRenderContent: boolean;
}

interface ShouldChainToOuterInput {
  deltaY: number;
  inner: HTMLDivElement;
}

interface ResolveTargetScrollTopInput {
  deltaY: number;
  outer: HTMLElement;
}

interface ResolveScrollChainMoveTargetInput {
  chainedToOuter: boolean;
  event: TouchEvent;
  inner: HTMLDivElement;
  outer: HTMLElement;
  startY: number;
}

export function useMobileScrollChain({
  innerElement,
  isMobile,
  outerScrollRef,
  shouldRenderContent,
}: UseMobileScrollChainInput): void {
  useEffect(() => {
    const elements = resolveScrollChainElements({
      innerElement,
      isMobile,
      outerScrollRef,
      shouldRenderContent,
    });
    if (!elements) return;
    const { inner, outer } = elements;

    let startY = 0;
    let chainedToOuter = false;

    const onTouchStart = (event: TouchEvent): void => {
      const touchClientY = readTouchClientY(event);
      if (touchClientY == null) return;

      startY = touchClientY;
      chainedToOuter = false;
    };

    const onTouchMove = (event: TouchEvent): void => {
      const targetScrollTop = resolveScrollChainMoveTarget({
        chainedToOuter,
        event,
        inner,
        outer,
        startY,
      });
      if (targetScrollTop == null) return;

      event.preventDefault();
      chainedToOuter = true;
      outer.scrollTo({ top: targetScrollTop, behavior: "auto" });
    };

    inner.addEventListener("touchstart", onTouchStart, { passive: true });
    inner.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      inner.removeEventListener("touchstart", onTouchStart);
      inner.removeEventListener("touchmove", onTouchMove);
    };
  }, [innerElement, isMobile, outerScrollRef, shouldRenderContent]);
}

function resolveScrollChainElements({
  innerElement,
  isMobile,
  outerScrollRef,
  shouldRenderContent,
}: ResolveScrollChainElementsInput): ScrollChainElements | null {
  if (!shouldAttachMobileScrollChain({ isMobile, shouldRenderContent })) {
    return null;
  }

  return resolvePresentScrollElements({
    innerElement,
    outerElement: resolveOuterScrollElement({ outerScrollRef }),
  });
}

function shouldAttachMobileScrollChain({
  isMobile,
  shouldRenderContent,
}: ShouldAttachMobileScrollChainInput): boolean {
  return [isMobile, shouldRenderContent].every(Boolean);
}

function resolvePresentScrollElements({
  innerElement,
  outerElement,
}: ResolvePresentScrollElementsInput): ScrollChainElements | null {
  if (!innerElement) return null;
  if (!outerElement) return null;

  return {
    inner: innerElement,
    outer: outerElement,
  };
}

function resolveOuterScrollElement({
  outerScrollRef,
}: ResolveOuterScrollElementInput): HTMLElement | null {
  return outerScrollRef?.current ?? null;
}

function readTouchClientY(event: TouchEvent): number | null {
  return event.touches[0]?.clientY ?? null;
}

function resolveScrollChainMoveTarget({
  chainedToOuter,
  event,
  inner,
  outer,
  startY,
}: ResolveScrollChainMoveTargetInput): number | null {
  if (chainedToOuter) return null;

  const touchClientY = readTouchClientY(event);
  if (touchClientY == null) return null;

  const deltaY = startY - touchClientY;
  if (!shouldChainToOuter({ deltaY, inner })) return null;

  return resolveTargetScrollTop({ deltaY, outer });
}

function shouldChainToOuter({
  deltaY,
  inner,
}: ShouldChainToOuterInput): boolean {
  const { atBottom, atTop } = getScrollBoundaryState(inner);

  return (deltaY < 0 && atTop) || (deltaY > 0 && atBottom);
}

function getScrollBoundaryState(inner: HTMLDivElement): ScrollBoundaryState {
  const { scrollTop, scrollHeight, clientHeight } = inner;

  return {
    atTop: scrollTop <= CWITNESS_SCROLL_BOUNDARY_PX,
    atBottom:
      scrollTop + clientHeight >= scrollHeight - CWITNESS_SCROLL_BOUNDARY_PX,
  };
}

function resolveTargetScrollTop({
  deltaY,
  outer,
}: ResolveTargetScrollTopInput): number | null {
  const direction = deltaY > 0 ? 1 : -1;
  const sectionHeight = outer.clientHeight;
  const outerScrollTopBefore = outer.scrollTop;
  const currentSection = Math.round(outerScrollTopBefore / sectionHeight);
  const maxSection = Math.max(
    0,
    Math.round((outer.scrollHeight - sectionHeight) / sectionHeight),
  );
  const targetSection = Math.min(
    maxSection,
    Math.max(0, currentSection + direction),
  );
  if (targetSection === currentSection) {
    return null;
  }

  return targetSection * sectionHeight;
}
